import {useMemo, useState, type ReactNode} from 'react';
import {
  Users,
  Users2,
  Building2,
  ChevronDown,
  ChevronRight,
  Workflow,
  Network,
  List,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {Card, CardContent} from '@common-web/components/ui/card';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {PageHeader, usePageHeaderVariant} from '@common-web/components/page-header';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {ACCOUNTS_SUB_STAGES, CANONICAL_SUB_STAGE_PHASE} from '@common/accountsView';
import {useNewBrokerUi} from '@client/hooks/useNewBrokerUi';
import {BrokerTeamRedesign} from '@client/pages/broker/BrokerTeamRedesign';

// Prospect stage = the LEAD phase minus the distinct SWOT Call stage. Kept in
// sync with the Accounts "Prospects" count (broker.getTeam), which excludes
// SWOT Call the same way.
const PROSPECT_SUB_STAGES = ACCOUNTS_SUB_STAGES.filter(
  (s) => CANONICAL_SUB_STAGE_PHASE[s] === 'LEAD' && s !== 'SWOT_CALL',
);

type ViewMode = 'tree' | 'list';

interface TeamBroker {
  id: string;
  name: string;
  email: string | null;
  parentAgentId: string | null;
  parentName: string | null;
  depth: number;
  isSelf: boolean;
  isActive: boolean;
  isSuspended: boolean;
  prospectCount: number;
  onboardingCount: number;
  dealCount: number;
  activeGroupCount: number;
  memberCount: number;
  estimatedMonthlyCommission: number;
}

interface TeamNode extends TeamBroker {
  children: TeamNode[];
}

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);

function rolePillFor(node: {isSelf: boolean; children: TeamNode[]}): string {
  if (node.isSelf) return 'You';
  return node.children.length > 0 ? 'Upline' : 'Individual';
}

function BrokerNodeCard({
  node,
  onViewGroups,
  onViewPipeline,
  className,
}: {
  node: TeamNode;
  onViewGroups: () => void;
  onViewPipeline: () => void;
  className?: string;
}) {
  const role = rolePillFor(node);
  return (
    <div
      className={cn(
        'w-[280px] rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:border-brand-blue hover:shadow-md',
        className,
      )}
    >
      <div className='mb-3 flex items-center gap-3'>
        <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-navy text-xs font-bold text-white'>
          {initialsOf(node.name)}
        </div>
        <div className='min-w-0 flex-1'>
          <p className='truncate text-sm font-semibold text-brand-navy'>{node.name}</p>
          <span
            className={cn(
              'mt-0.5 inline-block rounded-full px-2 py-[1px] text-[10px] font-semibold uppercase tracking-wide',
              role === 'You'
                ? 'bg-brand-blue/10 text-brand-blue'
                : role === 'Upline'
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-gray-100 text-brand-gray',
            )}
          >
            {role}
          </span>
        </div>
      </div>
      {node.email && (
        <p className='mb-3 truncate text-xs text-brand-gray'>{node.email}</p>
      )}
      <div className='mb-3 flex items-center gap-4 border-t border-gray-100 pt-3 text-xs text-brand-gray'>
        <span className='inline-flex items-center gap-1' title='Prospects'>
          <Users className='h-3.5 w-3.5' />
          {node.prospectCount}
        </span>
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            onViewGroups();
          }}
          className='inline-flex items-center gap-1 font-medium text-brand-blue hover:underline'
          title='Active groups'
        >
          <Building2 className='h-3.5 w-3.5' />
          {node.activeGroupCount}
        </button>
      </div>
      <div className='flex items-center gap-3 border-t border-gray-100 pt-2'>
        <button
          type='button'
          onClick={onViewPipeline}
          className='text-xs font-medium text-brand-blue hover:underline'
        >
          View pipeline →
        </button>
        <button
          type='button'
          onClick={onViewGroups}
          className='ml-auto text-xs font-medium text-brand-gray hover:text-brand-navy'
        >
          View groups →
        </button>
      </div>
    </div>
  );
}

function TreeNodeView({
  node,
  depth,
  onViewGroups,
  onViewPipeline,
}: {
  node: TeamNode;
  depth: number;
  onViewGroups: (id: string) => void;
  onViewPipeline: (id: string) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const hasChildren = node.children.length > 0;

  return (
    <div className='flex flex-col items-center'>
      <div className='relative'>
        {depth > 0 && <div className='absolute -top-6 left-1/2 h-6 w-px bg-gray-300' />}
        <BrokerNodeCard
          node={node}
          onViewGroups={() => onViewGroups(node.id)}
          onViewPipeline={() => onViewPipeline(node.id)}
        />
        {hasChildren && (
          <button
            type='button'
            onClick={() => setCollapsed((c) => !c)}
            className='absolute -bottom-3 left-1/2 z-10 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200 bg-white text-brand-gray hover:bg-gray-50'
            aria-label={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight className='h-3 w-3' /> : <ChevronDown className='h-3 w-3' />}
          </button>
        )}
      </div>

      {hasChildren && !collapsed && (
        <div className='relative mt-8 flex gap-8'>
          <div
            className='absolute left-1/2 top-0 h-px -translate-x-1/2 bg-gray-300'
            style={{width: `${Math.max(0, (node.children.length - 1) * 312)}px`}}
          />
          {node.children.map((child) => (
            <TreeNodeView
              key={child.id}
              node={child}
              depth={depth + 1}
              onViewGroups={onViewGroups}
              onViewPipeline={onViewPipeline}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  iconClass,
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  iconClass: string;
}) {
  return (
    <Card className='border-0 shadow-sm'>
      <CardContent className='flex items-center justify-between gap-4 p-5'>
        <div className='min-w-0'>
          <p className='text-sm font-medium text-brand-gray'>{label}</p>
          <p className='mt-1 text-2xl font-bold tabular-nums text-brand-navy'>{value}</p>
        </div>
        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', iconClass)}>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

function ViewModeToggle({
  viewMode,
  onChange,
}: {
  viewMode: ViewMode;
  onChange: (mode: ViewMode) => void;
}) {
  const headerVariant = usePageHeaderVariant();
  const isNavy = headerVariant === 'navy';

  const containerClass = isNavy
    ? 'bg-white/10 backdrop-blur-sm'
    : 'bg-gray-100 border border-gray-200';

  const buttonClass = (active: boolean) =>
    cn(
      'inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition-colors',
      active
        ? 'bg-white text-brand-navy shadow-sm'
        : isNavy
          ? 'text-white/80 hover:text-white'
          : 'text-brand-gray hover:text-brand-navy',
    );

  return (
    <div className={cn('inline-flex rounded-full p-1', containerClass)}>
      <button type='button' onClick={() => onChange('tree')} className={buttonClass(viewMode === 'tree')}>
        <Network className='h-4 w-4' />
        Tree
      </button>
      <button type='button' onClick={() => onChange('list')} className={buttonClass(viewMode === 'list')}>
        <List className='h-4 w-4' />
        List
      </button>
    </div>
  );
}

/**
 * Broker team / downline hierarchy view. Tenants on the `newBrokerUi` flag
 * get the redesigned tree/list layout; everyone else keeps the legacy page.
 */
export const BrokerTeam = () => {
  if (useNewBrokerUi()) {
    return <BrokerTeamRedesign />;
  }
  return <LegacyBrokerTeam />;
};

const LegacyBrokerTeam = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const table = useDataTable({
    syncToUrl: false,
    defaultSort: {key: 'name', direction: 'asc'},
  });

  const {data, isLoading, isFetching, error} = trpc.broker.getTeam.useQuery(undefined, {
    refetchOnWindowFocus: false,
  });

  const tree = (data?.tree ?? []) as TeamNode[];
  const self = (data?.self ?? null) as TeamBroker | null;
  const downlineBrokers = useMemo(
    () => ((data?.brokers ?? []) as TeamBroker[]).filter((b) => !b.isSelf),
    [data?.brokers],
  );

  // The team table loads its whole downline at once, so sorting is client-side
  // off the shared table's sort state (server-side sort/pagination is unused).
  const sortedBrokers = useMemo(() => {
    const arr = [...downlineBrokers];
    const dir = table.sort?.direction === 'desc' ? -1 : 1;
    switch (table.sort?.key) {
      case 'prospects':
        return arr.sort((a, b) => (a.prospectCount - b.prospectCount) * dir);
      case 'onboarding':
        return arr.sort((a, b) => (a.onboardingCount - b.onboardingCount) * dir);
      case 'groups':
        return arr.sort((a, b) => (a.activeGroupCount - b.activeGroupCount) * dir);
      case 'name':
      default:
        return arr.sort((a, b) => a.name.localeCompare(b.name) * dir);
    }
  }, [downlineBrokers, table.sort]);

  // Deep link into the Accounts page pre-filtered on the downline broker (and an
  // optional lifecycle phase), opened in a new tab so the team view stays put.
  // Pipeline = broker filter only; the count columns each filter to their stage.
  const openInNewTab = (path: string) => window.open(path, '_blank', 'noopener,noreferrer');
  const openAccounts = (brokerId: string, phase?: 'ONBOARDING' | 'ACTIVE') =>
    openInNewTab(`/accounts?broker=${brokerId}${phase ? `&phase=${phase}` : ''}`);
  // Prospects targets the Prospect stage sub-stages (LEAD minus SWOT Call) so it
  // matches the "Prospects" count and doesn't sweep in SWOT Call accounts.
  const goToProspects = (brokerId: string) =>
    openInNewTab(`/accounts?broker=${brokerId}&subStage=${PROSPECT_SUB_STAGES.join(',')}`);
  const goToOnboarding = (brokerId: string) => openAccounts(brokerId, 'ONBOARDING');
  const goToGroups = (brokerId: string) => openAccounts(brokerId, 'ACTIVE');
  const goToPipeline = (brokerId: string) => openAccounts(brokerId);

  const columns: Column<TeamBroker>[] = [
    {
      id: 'name',
      header: 'Broker',
      sortable: true,
      render: (b) => (
        <div className='flex items-center gap-3'>
          <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-navy text-[11px] font-bold text-white'>
            {initialsOf(b.name)}
          </div>
          <div className='min-w-0'>
            <p className='truncate text-sm font-semibold text-brand-navy'>{b.name}</p>
            {b.email && <p className='truncate text-xs text-brand-gray'>{b.email}</p>}
          </div>
        </div>
      ),
    },
    {
      id: 'depth',
      header: 'Depth',
      render: (b) => (
        <span className='inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-gray-100 px-2 text-xs font-semibold text-brand-gray'>
          L{b.depth}
        </span>
      ),
    },
    {
      id: 'upline',
      header: 'Upline',
      render: (b) => <span className='text-sm text-brand-gray'>{b.parentName || '\u2014'}</span>,
    },
    {
      id: 'prospects',
      header: 'Prospects',
      sortable: true,
      align: 'right',
      render: (b) => (
        <button
          type='button'
          onClick={() => goToProspects(b.id)}
          className='text-sm font-medium tabular-nums text-brand-blue hover:underline'
        >
          {b.prospectCount}
        </button>
      ),
    },
    {
      id: 'onboarding',
      header: 'Onboarding',
      sortable: true,
      align: 'right',
      render: (b) => (
        <button
          type='button'
          onClick={() => goToOnboarding(b.id)}
          className='text-sm font-medium tabular-nums text-brand-blue hover:underline'
        >
          {b.onboardingCount}
        </button>
      ),
    },
    {
      id: 'groups',
      header: 'Active Groups',
      sortable: true,
      align: 'right',
      render: (b) => (
        <button
          type='button'
          onClick={() => goToGroups(b.id)}
          className='text-sm font-medium tabular-nums text-brand-blue hover:underline'
        >
          {b.activeGroupCount}
        </button>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      render: (b) => (
        <div className='flex items-center justify-end gap-2'>
          <button
            type='button'
            onClick={() => goToPipeline(b.id)}
            className='inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy transition-colors hover:bg-gray-50'
          >
            <Workflow className='h-3.5 w-3.5 text-brand-gray' />
            Pipeline
          </button>
          <button
            type='button'
            onClick={() => goToGroups(b.id)}
            className='inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-brand-navy transition-colors hover:bg-gray-50'
          >
            <Building2 className='h-3.5 w-3.5 text-brand-gray' />
            Groups
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title='My Team'
          description='Read-only view of your downline brokers and their books.'
          icon={<Users2 className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
        </PageHeader>

        <div className='mx-auto w-full max-w-7xl px-8 py-6'>
          {error ? (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          ) : isLoading ? (
            <div className='flex items-center justify-center py-24'>
              <Loader2 className='h-8 w-8 animate-spin text-brand-blue' />
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className='mb-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                <StatCard
                  label='Direct Downlines'
                  value={data?.stats.directDownlines ?? 0}
                  icon={<Users2 className='h-5 w-5 text-brand-blue' />}
                  iconClass='bg-brand-blue/10'
                />
                <StatCard
                  label='Total Team Size'
                  value={data?.stats.totalTeamSize ?? 0}
                  icon={<Users className='h-5 w-5 text-teal-600' />}
                  iconClass='bg-teal-50'
                />
                <StatCard
                  label='Team Active Groups'
                  value={data?.stats.teamActiveGroups ?? 0}
                  icon={<Building2 className='h-5 w-5 text-emerald-600' />}
                  iconClass='bg-emerald-50'
                />
              </div>

              {downlineBrokers.length === 0 ? (
                <Card className='border-0 shadow-sm'>
                  <CardContent className='flex flex-col items-center justify-center gap-2 py-16 text-center'>
                    <Users2 className='h-10 w-10 text-brand-gray' />
                    <p className='text-lg font-semibold text-brand-navy'>No downline brokers yet</p>
                    <p className='max-w-md text-sm text-brand-gray'>
                      When brokers are placed under you, you&apos;ll see their pipeline and live
                      groups here.
                    </p>
                  </CardContent>
                </Card>
              ) : viewMode === 'tree' ? (
                <div className='overflow-x-auto pt-4'>
                  <div className='flex min-w-max flex-col items-center'>
                    {self && (
                      <div className='flex flex-col items-center'>
                        <BrokerNodeCard
                          node={{...self, children: tree}}
                          className='border-2 border-brand-blue'
                          onViewGroups={() => goToGroups(self.id)}
                          onViewPipeline={() => goToPipeline(self.id)}
                        />
                        {tree.length > 0 && <div className='mt-1 h-6 w-px bg-gray-300' />}
                      </div>
                    )}
                    {tree.length > 0 && (
                      <div className='relative flex gap-8'>
                        <div
                          className='absolute left-1/2 top-0 h-px -translate-x-1/2 bg-gray-300'
                          style={{width: `${Math.max(0, (tree.length - 1) * 312)}px`}}
                        />
                        {tree.map((child) => (
                          <TreeNodeView
                            key={child.id}
                            node={child}
                            depth={1}
                            onViewGroups={goToGroups}
                            onViewPipeline={goToPipeline}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <DataTable<TeamBroker>
                  table={table}
                  columns={columns}
                  data={sortedBrokers}
                  rowKey={(b) => b.id}
                  title='Team Members'
                  entity={{singular: 'broker', plural: 'brokers'}}
                  emptyMessage='No downline brokers yet'
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
